(function () {
  const widgets = document.querySelectorAll('[data-gv-pdp-quick-order]');
  if (!widgets.length) return;

  function parseJson(value, fallback) {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error('[gv-pdp-quick-order] Unable to parse JSON payload', error);
      return fallback;
    }
  }

  widgets.forEach((widget) => {
    const label = widget.querySelector('[data-gv-list-label]');
    const listTrigger = widget.querySelector('[data-gv-list-trigger]');
    const listTriggerText = widget.querySelector('[data-gv-list-trigger-text]');
    const listMenu = widget.querySelector('[data-gv-list-menu]');
    const status = widget.querySelector('[data-gv-add-to-list-status]');
    const variantInput =
      widget.closest('form')?.querySelector('.product-variant-id') ||
      widget.closest('product-info')?.querySelector('.product-variant-id') ||
      widget.closest('section')?.querySelector('.product-variant-id') ||
      document.querySelector('.product-form__input .product-variant-id') ||
      document.querySelector('.product-variant-id');

    if (!label || !listTrigger || !listTriggerText || !listMenu || !variantInput) {
      console.warn('[gv-pdp-quick-order] Required element missing', {
        hasLabel: Boolean(label),
        hasListTrigger: Boolean(listTrigger),
        hasListTriggerText: Boolean(listTriggerText),
        hasListMenu: Boolean(listMenu),
        hasVariantInput: Boolean(variantInput),
      });
      return;
    }

    const state = {
      apiBase: widget.dataset.apiBase,
      apiKey: widget.dataset.apiKey,
      shopifyCustomerId: widget.dataset.shopifyCustomerId || '',
      locations: parseJson(widget.dataset.locations, []),
      accessParams: null,
      initialized: false,
      currentVariantId: null,
      savedLists: [],
      listItemsByListId: {},
      selectedListId: null,
      isMenuOpen: false,
      isCreateFormOpen: false,
      highlightedListId: null,
      highlightTimer: null,
      removedListId: null,
      removeTimer: null,
      isMutating: false,
    };

    function escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = String(value || '');
      return div.innerHTML;
    }

    function setStatus(message, isError) {
      if (!status) return;
      status.textContent = message || '';
      status.classList.toggle('is-error', Boolean(isError));
    }

    function getEventTargetElement(event) {
      if (event.target instanceof Element) return event.target;
      return event.target?.parentElement || null;
    }

    function setLoading(isLoading) {
      state.isMutating = Boolean(isLoading);
      listTrigger.disabled = state.isMutating || !state.initialized;
      widget.classList.toggle('is-loading', state.isMutating);
    }

    function clearAddedHighlight() {
      if (state.highlightTimer) {
        window.clearTimeout(state.highlightTimer);
        state.highlightTimer = null;
      }
      state.highlightedListId = null;
    }

    function clearRemovedHighlight() {
      if (state.removeTimer) {
        window.clearTimeout(state.removeTimer);
        state.removeTimer = null;
      }
      state.removedListId = null;
    }

    function clearListFeedback() {
      clearAddedHighlight();
      clearRemovedHighlight();
    }

    function markListAdded(listId) {
      clearListFeedback();
      state.highlightedListId = Number(listId);
      state.highlightTimer = window.setTimeout(() => {
        state.highlightedListId = null;
        state.highlightTimer = null;
        if (state.isMenuOpen) renderMenu();
      }, 1400);
    }

    function markListRemoved(listId) {
      clearListFeedback();
      state.removedListId = Number(listId);
      state.removeTimer = window.setTimeout(() => {
        state.removedListId = null;
        state.removeTimer = null;
        if (state.isMenuOpen) renderMenu();
      }, 1400);
    }

    function buildUrl(path, params) {
      const url = new URL(`${state.apiBase}${path}`);
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          url.searchParams.set(key, value);
        }
      });
      return url.toString();
    }

    async function apiFetch(path, method, params, body) {
      const response = await fetch(buildUrl(path, params), {
        method: method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-QuickOrder-ApiKey': state.apiKey,
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const text = await response.text();
        const error = new Error(text || `Quick order request failed (${response.status})`);
        error.status = response.status;
        throw error;
      }

      return response.status === 204 ? null : response.json();
    }

    function isNetworkOrCorsError(error) {
      return error instanceof TypeError && /fetch/i.test(error.message || '');
    }

    function savedListNameExists(name) {
      const normalizedName = String(name || '').trim().toLowerCase();
      if (!normalizedName) return false;

      return state.savedLists.some((list) => getListName(getListId(list)).trim().toLowerCase() === normalizedName);
    }

    function findAccessParams() {
      const location = state.locations.find((entry) => entry && entry.custId);
      if (!location) return null;

      return {
        custId: location.custId,
        token: location.token || '',
        shopifyCustomerId: state.shopifyCustomerId,
      };
    }

    function isVariantUnavailableError(error) {
      const message = String(error?.message || '').toLowerCase();
      return (
        message.includes('variant is not available for this company location') ||
        message.includes('variant not found')
      );
    }

    function isDefaultList(list) {
      return Boolean(list?.isDefault ?? list?.IsDefault);
    }

    function getListId(list) {
      return Number(list?.id ?? list?.Id);
    }

    function getListItemId(item) {
      return (
        item?.itemId ??
        item?.ItemId ??
        item?.listItemId ??
        item?.ListItemId ??
        item?.quickOrderListItemId ??
        item?.QuickOrderListItemId
      );
    }

    function getItemVariantId(item) {
      return item?.variantId ?? item?.VariantId;
    }

    function getListName(listId) {
      const list = state.savedLists.find((entry) => getListId(entry) === Number(listId));
      return list?.name || list?.Name || 'Saved list';
    }

    function getDefaultList() {
      return state.savedLists.find(isDefaultList) || state.savedLists[0] || null;
    }

    function getListItems(listId) {
      return state.listItemsByListId[Number(listId)] || [];
    }

    function findVariantListItem(listId) {
      return getListItems(listId).find((item) => String(getItemVariantId(item)) === state.currentVariantId);
    }

    function getCurrentMembershipListIds() {
      const variantId = String(state.currentVariantId || '');
      if (!variantId) return [];

      return state.savedLists
        .filter((entry) => {
          const items = getListItems(getListId(entry));
          return items.some((item) => String(getItemVariantId(item)) === variantId);
        })
        .map(getListId)
        .filter(Boolean);
    }

    function focusCreateListInput() {
      if (listMenu.hidden) return;
      window.setTimeout(() => {
        listMenu.querySelector('[data-create-list-name]')?.focus();
      }, 0);
    }

    function closeMenu() {
      state.isMenuOpen = false;
      state.isCreateFormOpen = false;
      clearListFeedback();
      listTrigger.setAttribute('aria-expanded', 'false');
      listMenu.hidden = true;
    }

    function openMenu() {
      if (!state.initialized || state.isMutating) return;
      renderMenu();
      state.isMenuOpen = true;
      listTrigger.setAttribute('aria-expanded', 'true');
      listMenu.hidden = false;
    }

    function toggleMenu() {
      if (state.isMenuOpen) {
        closeMenu();
        return;
      }
      openMenu();
    }

    function renderSummary() {
      const memberListIds = getCurrentMembershipListIds();

      if (!memberListIds.length) {
        label.textContent = 'Add to ';
        listTriggerText.textContent = 'List';
        return;
      }

      label.textContent = 'Saved to';

      if (memberListIds.length === 1) {
        listTriggerText.textContent = getListName(memberListIds[0]);
        return;
      }

      listTriggerText.textContent = `${memberListIds.length} Lists`;
    }

    function renderMenu() {
      listMenu.innerHTML = '';
      const memberListIds = getCurrentMembershipListIds();

      if (!state.savedLists.length) {
        const emptyMessage = document.createElement('div');
        emptyMessage.className = 'gv-pdp-quick-order__menu-empty';
        emptyMessage.textContent = 'No saved lists yet.';
        listMenu.appendChild(emptyMessage);
      }

      state.savedLists.forEach((list) => {
        const listId = getListId(list);
        if (!listId) return;

        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'gv-pdp-quick-order__menu-option';
        option.dataset.listId = String(listId);

        if (memberListIds.includes(listId)) {
          option.classList.add('is-saved');
        }

        if (Number(state.selectedListId) === listId) {
          option.classList.add('is-selected');
        }

        if (Number(state.highlightedListId) === listId && memberListIds.includes(listId)) {
          option.classList.add('is-added-feedback');
        }

        if (Number(state.removedListId) === listId && !memberListIds.includes(listId)) {
          option.classList.add('is-removed-feedback');
        }

        const listName = escapeHtml(list.name || list.Name || 'Saved list');
        const defaultBadge = isDefaultList(list)
          ? '<span class="gv-pdp-quick-order__default-badge" aria-label="Default list">&#9733;</span>'
          : '';
        const checkmark = memberListIds.includes(listId) ? '&#10003;' : '';

        option.innerHTML = `<span class="gv-pdp-quick-order__menu-option-content">
          <span>${listName}</span>
          ${defaultBadge}
        </span><span class="gv-pdp-quick-order__menu-option-check" aria-hidden="true">${checkmark}</span>`;
        listMenu.appendChild(option);
      });

      const divider = document.createElement('div');
      divider.className = 'gv-pdp-quick-order__menu-divider';
      listMenu.appendChild(divider);

      if (!state.isCreateFormOpen) {
        const createToggle = document.createElement('button');
        createToggle.type = 'button';
        createToggle.className = 'gv-pdp-quick-order__menu-option gv-pdp-quick-order__create-toggle';
        createToggle.dataset.createListToggle = 'true';
        createToggle.textContent = '+ Create new list';
        listMenu.appendChild(createToggle);
      } else {
        const inputId = `gv-pdp-create-list-${state.shopifyCustomerId || 'customer'}`;
        const createForm = document.createElement('div');
        createForm.className = 'gv-pdp-quick-order__create-form';
        createForm.dataset.createListForm = 'true';
        createForm.innerHTML = `
          <input
            id="${inputId}"
            class="gv-pdp-quick-order__create-input"
            type="text"
            data-create-list-name
            placeholder="Enter list name"
            aria-label="New list name"
          />
          <div class="gv-pdp-quick-order__create-actions">
            <button class="gv-pdp-quick-order__create-confirm" type="button" data-create-list-confirm>Create</button>
          </div>
        `;
        listMenu.appendChild(createForm);
      }

      const orderFormLink = document.createElement('a');
      orderFormLink.className = 'gv-pdp-quick-order__order-form-link';
      orderFormLink.href = '/pages/order-form';
      orderFormLink.textContent = 'View lists \u2192';
      listMenu.appendChild(orderFormLink);

      listTrigger.disabled = !state.initialized || state.isMutating;
    }

    function syncVariantState() {
      state.currentVariantId = String(variantInput.value || '');
      clearListFeedback();
      renderSummary();
      renderMenu();
      setStatus('', false);
    }

    async function resolveSavedLists() {
      const quickOrder = await apiFetch('', 'GET', state.accessParams);
      const savedLists = quickOrder?.savedLists || [];
      if (!savedLists.length) return [];

      const defaultSavedList = savedLists.find(isDefaultList) || savedLists[0];
      const defaultSavedListId = getListId(defaultSavedList);

      if (defaultSavedListId && !savedLists.some(isDefaultList)) {
        await apiFetch(`/lists/${defaultSavedListId}/default`, 'PUT', state.accessParams);
        defaultSavedList.isDefault = true;
        if ('IsDefault' in defaultSavedList) defaultSavedList.IsDefault = true;
      }

      return savedLists;
    }

    async function loadListItems() {
      const results = await Promise.all(
        state.savedLists.map(async (list) => {
          const listId = getListId(list);
          const detail = await apiFetch(`/lists/${listId}`, 'GET', state.accessParams);
          return {
            listId,
            items: detail?.items || [],
          };
        })
      );

      results.forEach((entry) => {
        state.listItemsByListId[entry.listId] = entry.items;
      });
    }

    async function refreshListItems(listId) {
      const detail = await apiFetch(`/lists/${listId}`, 'GET', state.accessParams);
      state.listItemsByListId[Number(listId)] = detail?.items || [];
      return state.listItemsByListId[Number(listId)];
    }

    async function addVariantToList(listId) {
      await apiFetch(`/lists/${listId}/items`, 'POST', state.accessParams, {
        variantId: Number(state.currentVariantId),
        quantity: 1,
      });

      await refreshListItems(listId);

      if (findVariantListItem(listId)) return;

      state.listItemsByListId[Number(listId)] = [
        ...getListItems(listId),
        { variantId: Number(state.currentVariantId), quantity: 1 },
      ];
    }

    async function removeVariantFromList(listId) {
      await refreshListItems(listId);

      let item = findVariantListItem(listId);
      let itemId = getListItemId(item);

      if (itemId === null || itemId === undefined || itemId === '') {
        throw new Error('Unable to find this saved list item.');
      }

      try {
        await apiFetch(`/lists/${listId}/items/${itemId}`, 'DELETE', state.accessParams);
      } catch (error) {
        if (error.status !== 404) throw error;

        await refreshListItems(listId);
        const refreshedItem = findVariantListItem(listId);
        const refreshedItemId = getListItemId(refreshedItem);

        if (!refreshedItem) return;
        if (!refreshedItemId || String(refreshedItemId) === String(itemId)) throw error;

        await apiFetch(`/lists/${listId}/items/${refreshedItemId}`, 'DELETE', state.accessParams);
      }

      await refreshListItems(listId);
    }

    async function createSavedList(name) {
      const trimmedName = String(name || '').trim();
      if (!trimmedName) {
        throw new Error('Enter a list name to continue.');
      }

      const hadNoSavedLists = state.savedLists.length === 0;
      const created = await apiFetch('/lists', 'POST', state.accessParams, { name: trimmedName });
      const createdListId = Number(created?.id ?? created?.Id);

      if (!createdListId) {
        throw new Error('Saved list was created, but no list id was returned.');
      }

      const createdList = {
        ...(created || {}),
        id: createdListId,
        name: created?.name || created?.Name || trimmedName,
      };

      if (hadNoSavedLists) {
        await apiFetch(`/lists/${createdListId}/default`, 'PUT', state.accessParams);
        createdList.isDefault = true;
        if ('IsDefault' in createdList) createdList.IsDefault = true;
      }

      state.savedLists.push(createdList);
      state.listItemsByListId[createdListId] = [];
      state.selectedListId = createdListId;
      return createdListId;
    }

    async function submitCreateList() {
      if (state.isMutating) return;

      const nameInput = listMenu.querySelector('[data-create-list-name]');
      const listName = String(nameInput?.value || '').trim();

      if (!listName) {
        setStatus('Enter a list name to continue.', true);
        nameInput?.focus();
        return;
      }

      if (savedListNameExists(listName)) {
        setStatus('A saved list with this name already exists.', true);
        nameInput?.focus();
        return;
      }

      if (!state.currentVariantId) {
        closeMenu();
        setStatus('Select a variant to add it to a saved list.', true);
        return;
      }

      let createdListId = null;

      try {
        setLoading(true);
        createdListId = await createSavedList(listName);

        try {
          await addVariantToList(createdListId);
        } catch (error) {
          renderSummary();
          renderMenu();

          if (isVariantUnavailableError(error)) {
            setStatus(`Created ${getListName(createdListId)}, but this variant is not available and was not saved.`, true);
          } else {
            setStatus(`Created ${getListName(createdListId)}, but unable to add this item.`, true);
          }
          return;
        }

        state.isCreateFormOpen = false;
        markListAdded(createdListId);
        renderSummary();
        renderMenu();
        setStatus('', false);
      } catch (error) {
        console.error(error);
        const message =
          error.message === 'Enter a list name to continue.'
            ? error.message
            : error.status === 409 && error.message
              ? error.message
            : isNetworkOrCorsError(error)
              ? 'Unable to reach saved lists right now. Please try again.'
              : 'Unable to create this saved list. Please try again.';
        setStatus(message, true);
        focusCreateListInput();
      } finally {
        setLoading(false);
      }
    }

    async function initialize() {
      state.accessParams = findAccessParams();

      if (!state.apiBase || !state.apiKey || !state.accessParams) {
        listTrigger.disabled = true;
        setStatus('Quick order lists are unavailable for this account.', true);
        return;
      }

      try {
        state.savedLists = await resolveSavedLists();
        await loadListItems();

        const defaultList = getDefaultList();
        state.selectedListId = defaultList ? getListId(defaultList) : null;
        state.currentVariantId = String(variantInput.value || '');
        state.initialized = true;

        renderSummary();
        renderMenu();

        if (!state.savedLists.length) {
          setStatus('Create a saved list from here to add this item.', false);
        }
      } catch (error) {
        console.error(error);
        listTrigger.disabled = true;
        setStatus('Unable to load your quick order lists right now.', true);
      }
    }

    listTrigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      toggleMenu();
    });

    listMenu.addEventListener('click', async (event) => {
      event.stopPropagation();
      const target = getEventTargetElement(event);
      if (!target) return;

      if (target.closest('[data-create-list-toggle]')) {
        state.isCreateFormOpen = true;
        renderMenu();
        focusCreateListInput();
        return;
      }

      if (target.closest('[data-create-list-confirm]')) {
        await submitCreateList();
        return;
      }

      const option = target.closest('[data-list-id]');
      if (!option || state.isMutating) return;

      const selectedListId = Number(option.dataset.listId);
      if (!selectedListId || !state.currentVariantId) {
        closeMenu();
        setStatus('Select a variant to add it to a saved list.', true);
        return;
      }

      state.selectedListId = selectedListId;
      const alreadySaved = Boolean(findVariantListItem(selectedListId));

      try {
        setLoading(true);

        if (alreadySaved) {
          await removeVariantFromList(selectedListId);
          markListRemoved(selectedListId);
          renderSummary();
          renderMenu();
          setStatus('', false);
          return;
        }

        await addVariantToList(selectedListId);
        markListAdded(selectedListId);
        renderSummary();
        renderMenu();
        setStatus('', false);
      } catch (error) {
        console.error(error);
        if (isVariantUnavailableError(error)) {
          setStatus('This variant is not available and cannot be saved.', true);
        } else {
          setStatus('Unable to update this saved list. Please try again.', true);
        }
      } finally {
        setLoading(false);
      }
    });

    listMenu.addEventListener('keydown', async (event) => {
      if (event.key === 'Enter' && event.target.matches('[data-create-list-name]')) {
        event.preventDefault();
        await submitCreateList();
        return;
      }

      if (event.key === 'Escape') {
        closeMenu();
      }
    });

    document.addEventListener('click', (event) => {
      if (!state.isMenuOpen) return;
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      if (widget.contains(event.target) || path.includes(widget)) return;
      closeMenu();
    });

    document.addEventListener('change', (event) => {
      if (!event.target.closest('variant-selects')) return;
      window.setTimeout(syncVariantState, 50);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        window.setTimeout(syncVariantState, 50);
      });
    }

    initialize();
  });
})();
